import {Util} from './Util';
import {ValidationErrors, WorklogConstructorParams, WorklogJSON, WorklogStatus} from '../types/storage';
import {I18nService} from "./I18nService";

export class Worklog {
  public id: string;
  public jiraId: string | null = null;
  public issueKey: string;
  public description: string;
  public start: Date;
  public end: Date | null;
  public status: WorklogStatus;
  public error: string;
  public createdAt: Date;
  public updatedAt: Date;
  public syncedAt: Date | null;
  public pendingDelete: boolean;

  constructor(
    {
      id,
      jiraId,
      issueKey,
      description,
      start,
      end,
      status = 'local',
      error = '',
      createdAt,
      updatedAt,
      syncedAt
    }: WorklogConstructorParams) {
    this.id = id || Util.generateId();
    this.jiraId = jiraId || null;
    this.issueKey = (issueKey || '').trim();
    this.description = (description || '').trim();
    this.start = start instanceof Date ? start : new Date(start);
    this.end = end ? (end instanceof Date ? end : new Date(end)) : null;
    this.status = status;
    this.error = error || '';
    this.createdAt = createdAt ? new Date(createdAt) : new Date();
    this.updatedAt = updatedAt ? new Date(updatedAt) : new Date();
    this.syncedAt = syncedAt ? new Date(syncedAt) : null;

    // Initialize pendingDelete from raw input if present; default to false
    const raw: any = (arguments as unknown as any[])[0] || {};
    this.pendingDelete = Boolean(raw.pendingDelete);
  }

  get durationSec(): number {
    const end = this.end || new Date();
    return Math.max(0, Math.floor((end.getTime() - this.start.getTime()) / 1000));
  }

  get isComplete(): boolean {
    return !!(this.issueKey && this.description);
  }

  static toJSON(w: Worklog): WorklogJSON {
    return {
      id: w.id,
      issueKey: w.issueKey,
      description: w.description,
      jiraId: w.jiraId,
      start: w.start.toISOString(),
      end: w.end ? w.end.toISOString() : null,
      status: w.status,
      error: w.error,
      createdAt: w.createdAt.toISOString(),
      updatedAt: w.updatedAt.toISOString(),
      syncedAt: w.syncedAt ? w.syncedAt.toISOString() : null,
      pendingDelete: w.pendingDelete || false
    } as any;
  }

  validate(i18n: I18nService): ValidationErrors {
    const errors: ValidationErrors = {};
    const issueRe = /^[A-Z][A-Z0-9_]+-[1-9][0-9]*$/i;
    if (!this.issueKey || !issueRe.test(this.issueKey)) {
      errors.issueKey = i18n.t('invalidIssueKey');
    }
    if (!this.description) {
      errors.description = i18n.t('required');
    }
    if (!this.start) {
      errors.start = i18n.t('required');
    }
    const end = this.end;
    if (!end) {
      errors.end = i18n.t('required');
    }
    if (this.start && end && end <= this.start) {
      errors.range = i18n.t('invalidTimeRange');
    }
    const dur = this.start && end ? Math.floor((end.getTime() - this.start.getTime()) / 1000) : 0;
    if (dur > 18 * 3600) {
      errors.max = i18n.t('durationTooLong');
    }
    if (dur > 0 && dur < 60) {
      errors.min = i18n.t('durationTooShort');
    }
    return errors;
  }
}